// RTSQA-339 (2026-05-14): License Detail > Activate Seats > Activation History.
//
// LicenseDetailModal 의 우측 column 하단에서 토글로 노출. activationLogs 배열을
// 그대로 받아 4-column 테이블 (Type / Timestamp / PC / Actor) 로 렌더한다.
// 각 항목 schema: { rtid, created, type, actor, pcinfo } — DeactivateOtherPC 가
// list_append 하는 voucherSk 레코드 최상위 배열의 element.
//
// 가드:
//  - typeof rtid !== 'string' 또는 빈 문자열 → row skip (PR #221 review #12 패턴).
//  - logs 비배열 → empty 처리.
//  - type 이 매핑되지 않는 값 → secondary chip + 원본 라벨 표시 (fail-loud).
//
// 스타일:
//  - 길어질 수 있는 이력 흡수 — wrapper max-height: 30vh + sticky thead.
//  - table-sm + table-bordered + table-xs (Active Seats 와 통일).

import React, { useMemo } from 'react';
import { Translate as t } from '../../config/Translate';
import { renderHeader } from './columns';
// IMP-437 (D): error.reason 원문 노출 방지 — 같은 레포의 SSoT(LicenseDetailModal 이미 사용 중)를
// 이 경로만 미사용이던 것을 해소. fallbackKey 는 기존 폴백(common.status.error)과 동일하게 유지.
import { resolvePcFailText } from '../../lib/pcActivationResult';
// IMP-438: 날짜 포맷 SSoT — Number.isFinite 가드 포함, 앱 언어 locale 로 렌더.
import { formatLicenseDate } from '../../lib/licenseHelper';

const TYPE_BADGE = {
    activation: { variant: 'bg-success', i18nKey: 'pcActivation.historyType.activation' },
    deactivation: { variant: 'bg-danger', i18nKey: 'pcActivation.historyType.deactivation' },
};

// IMP-167 (PR #342 review): license/ 테이블 공용 컬럼 DSL 통일. sticky thead 는
// 모든 컬럼 공통이라 STICKY_TH 로 추출. 컬럼 순서는 RTSQA-339 round 10 결정
// (PC → License Type → At → Type chip → Actor — Active Seats 와 일관).
const STICKY_TH = { position: 'sticky', top: 0, background: '#fff', zIndex: 1 };
const COLUMN_HEADERS = [
    { key: 'pc', label: 'pcActivation.historyColPc', style: STICKY_TH },
    { key: 'licenseType', label: 'pcActivation.historyColLicenseType', style: { ...STICKY_TH, whiteSpace: 'nowrap' } },
    { key: 'at', label: 'pcActivation.historyColAt', style: { ...STICKY_TH, whiteSpace: 'nowrap' } },
    { key: 'type', label: 'pcActivation.historyColType', style: STICKY_TH },
    { key: 'actor', label: 'pcActivation.historyColActor', style: STICKY_TH },
];

function renderTypeChip(type) {
    const mapped = TYPE_BADGE[type];
    if (mapped) {
        return (
            <span className={`badge ${mapped.variant} text-white`}>
                {t(mapped.i18nKey)}
            </span>
        );
    }
    // fail-loud: 미정의 type 은 secondary + 원본 라벨 노출 (BE/FE SSoT drift 감지).
    return (
        <span className="badge bg-secondary text-white">
            {typeof type === 'string' && type.length > 0 ? type : '-'}
        </span>
    );
}

// RTSQA-339 round 7 (2026-05-14, 사용자 요청): licenseTypeLabel prop 추가.
// activationLogs schema 에 라이선스 type snapshot 이 없어 modal 의 license.type
// (Initial / Renewal) 을 호출자에서 prefill 하여 모든 row 에 동일 표시.
//
// RTSQA-339 round 19 (사용자 요청 2026-05-14): actor (userId) 를 사용자 친화 라벨로
// 표시. activationLogs[].actor 는 string (= voucher_license.userId). 호출자가
// resolveActorLabel(rawActor) 로 groupName / email 매핑.
const PCActivationHistoryTable = ({ logs, loading, error, licenseTypeLabel, resolveActorLabel }) => {
    // created 내림차순. 같은 created 값일 때는 stable sort 보장 (Array.prototype.sort
    // ES2019+ stable). 원본 배열 변형 차단을 위해 spread 후 sort.
    const sortedLogs = useMemo(() => {
        if (!Array.isArray(logs)) return [];
        return [...logs].sort((a, b) => {
            const ac = Number.isFinite(a?.created) ? a.created : 0;
            const bc = Number.isFinite(b?.created) ? b.created : 0;
            return bc - ac;
        });
    }, [logs]);

    // PR 사전 리뷰 Minor#1: resolvePcFailText 는 매핑 미스 시 내부에서 console.warn 을 찍는다
    //   (resolvePcFailMessage, IMP-421 §7). 기존 소비처는 전부 이벤트 핸들러라 1회성이었지만
    //   이 배너는 렌더 본문이라, 부모의 무관한 state 변경으로 리렌더될 때마다 같은 warn 이
    //   반복돼 로그가 오염된다. error 동일성으로 메모해 "실패 1건당 1회" 로 되돌린다.
    const errorText = useMemo(() => (error ? resolvePcFailText(error, t) : null), [error]);

    if (loading) {
        return (
            <div className="text-muted small" data-testid="pc-activation-history-loading">
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                {t('common.status.loading')}
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger py-1 small mb-0" data-testid="pc-activation-history-error">
                {errorText}
            </div>
        );
    }

    if (sortedLogs.length === 0) {
        return (
            <div className="text-muted small" data-testid="pc-activation-history-empty">
                {t('pcActivation.noActivationHistory')}
            </div>
        );
    }

    return (
        <div
            className="table-responsive"
            style={{ maxHeight: '30vh', overflowY: 'auto' }}
            data-testid="pc-activation-history-table"
        >
            <table className="table table-sm table-bordered table-xs mb-0">
                <thead>
                    {/* RTSQA-339 round 7/10: License Type 컬럼(호출자 licenseTypeLabel prefill,
                        Initial/Renewal) + 컬럼 순서를 Active Seats 와 일치(PC → License Type →
                        At → Type chip(Status 위치) → Actor). 헤더 정의는 COLUMN_HEADERS 참조. */}
                    <tr>{renderHeader(COLUMN_HEADERS)}</tr>
                </thead>
                <tbody>
                    {sortedLogs.map((log, idx) => {
                        // PR #221 review #12: rtid 가 string 아니거나 빈 값이면 row skip.
                        // BE 응답에 rtid 가 누락된 경우 sort 후에도 안전하게 skip.
                        if (typeof log?.rtid !== 'string' || log.rtid.length === 0) return null;
                        const rtid = log.rtid;
                        const rtidShort = rtid.length > 12 ? `${rtid.slice(0, 8)}…` : rtid;
                        const pcLabel = log?.pcinfo?.hostname || rtidShort;
                        const pcLocalIp = log?.pcinfo?.localip || null;
                        // RTSQA-339 round 19: actor 는 Confluence schema 상 string (userId).
                        // 일부 legacy 데이터는 { id } object 일 수도 있어 양쪽 모두 처리.
                        // resolveActorLabel 가 있으면 호출 (호출자가 groupName/email 매핑).
                        const rawActor = typeof log?.actor === 'string'
                            ? log.actor
                            : (log?.actor?.id || null);
                        const actorLabel = (typeof resolveActorLabel === 'function' && rawActor)
                            ? (resolveActorLabel(rawActor) || rawActor)
                            : (rawActor || '-');
                        // key — log 객체에 _id 없으므로 rtid + created + idx 조합 (sort 후 중복 방어).
                        const rowKey = `${rtid}-${log?.created ?? '_'}-${idx}`;
                        // RTSQA-339 round 10: 컬럼 순서 PC → License Type → At → Type → By
                        // (Active Seats 와 일관).
                        return (
                            <tr key={rowKey} className="align-middle">
                                {/* PR #221 review #17 동일 정책: title 에도 단축값만 노출 —
                                    hover/스크린리더/context menu 에서 full rtid 표면화 차단. */}
                                <td className="small" title={rtidShort}>
                                    <div>{pcLabel}</div>
                                    {pcLocalIp && (
                                        <div className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>
                                            {pcLocalIp}
                                        </div>
                                    )}
                                </td>
                                {/* RTSQA-339 round 7: License Type — modal 단일 라이선스이므로
                                    모든 row 가 동일 값 (호출자에서 isRenewalLicense 로 결정). */}
                                <td className="small">{licenseTypeLabel || '-'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatLicenseDate(log?.created)}</td>
                                <td>{renderTypeChip(log?.type)}</td>
                                <td className="small">{actorLabel}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default PCActivationHistoryTable;
